import { useCallback } from 'react';
import { supabase } from '../lib/supabase';

// IRS standard mileage rate 2025
export const IRS_RATE_PER_MILE = 0.70;

export function useDriveLog() {
  const getTrips = useCallback(async ({ userId, year, month } = {}) => {
    let query = supabase
      .from('drive_logs')
      .select('*')
      .eq('user_id', userId)
      .order('start_time', { ascending: false });

    if (year && month) {
      const start = new Date(year, month - 1, 1).toISOString();
      const end = new Date(year, month, 0, 23, 59, 59).toISOString();
      query = query.gte('start_time', start).lte('start_time', end);
    } else if (year) {
      const start = new Date(year, 0, 1).toISOString();
      const end = new Date(year, 11, 31, 23, 59, 59).toISOString();
      query = query.gte('start_time', start).lte('start_time', end);
    }

    const { data, error } = await query;
    if (error) throw error;
    return data || [];
  }, []);

  const createTrip = useCallback(async (tripData) => {
    const { data, error } = await supabase
      .from('drive_logs')
      .insert(tripData)
      .select()
      .single();
    if (error) throw error;
    return data;
  }, []);

  const updateTrip = useCallback(async (id, updates) => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('Not authenticated');
    const { data, error } = await supabase
      .from('drive_logs')
      .update(updates)
      .eq('id', id)
      .eq('user_id', user.id)
      .select()
      .single();
    if (error) throw error;
    return data;
  }, []);

  const deleteTrip = useCallback(async (id) => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('Not authenticated');
    const { error } = await supabase
      .from('drive_logs')
      .delete()
      .eq('id', id)
      .eq('user_id', user.id);
    if (error) throw error;
  }, []);

  return { getTrips, createTrip, updateTrip, deleteTrip };
}

// Haversine distance between two lat/lng points in miles
export function haversineDistance(lat1, lon1, lat2, lon2) {
  const R = 3958.8; // Earth radius in miles
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLon = ((lon2 - lon1) * Math.PI) / 180;
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos((lat1 * Math.PI) / 180) *
    Math.cos((lat2 * Math.PI) / 180) *
    Math.sin(dLon / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}
